import { showError } from '@calendly/alert';
import { I18n } from '@calendly/i18n';
import { Logger } from '@calendly/logging-service';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import { useDisableScrollBody } from '@calendly/ui/hooks/use-disable-scroll-body';
import { useFocusLock } from '@calendly/ui/hooks/use-focus-lock';
import classNames from 'classnames';
import { forwardRef, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useProductRefreshAppShell } from 'features/app_shell';
import { useLeftNavigationContext } from 'features/left_navigation';
import { BareOutlet, URL_STATE_PREFIX } from 'features/scoped_url_state';
import { useRightRail } from './hooks/use_right_rail';
import { routes as defaultRoutes } from './routes';
const wrapperIsOpenCss = "wxdpn54";
const wrapperIsPhoneCss = "ww5tvry";
const wrapperCss = "wr4aa1r";
const Wrapper = forwardRef((props, ref) => {
  const {
    isOpen,
    isPhone,
    as: AsComponent,
    railWidth,
    pushWidth,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const styleProp = {
    style: {
      '--rail-width': railWidth,
      '--push-width': pushWidth
    }
  };
  return <Component ref={ref} {...restProps} {...styleProp} className={classNames(wrapperCss, {
    [wrapperIsOpenCss]: isOpen,
    [wrapperIsPhoneCss]: isPhone
  }, props.className)} />;
});
const containerIsOpenCss = "c1thswmg";
const containerIsPhoneCss = "cyq9w3n";

/** Legacy shell only; product-refresh shell lays the rail in `#shell-side-panel`. */
const containerLegacyViewportCss = "c1peykwu";
const containerCss = "c69w0vy";
const Container = forwardRef((props, ref) => {
  const {
    isOpen,
    isPhone,
    as: AsComponent,
    useLegacyViewportPosition,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(containerCss, {
    [containerIsOpenCss]: isOpen,
    [containerIsPhoneCss]: isPhone,
    [containerLegacyViewportCss]: useLegacyViewportPosition
  }, props.className)} />;
});
const contentCss = "c1cljqc0";
const Content = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'aside';
  return <Component ref={ref} {...restProps} className={classNames(contentCss, props.className)} />;
});
const RailRenderer = ({
  pane,
  component: Component,
  useGuard,
  ...props
}) => {
  const {
    close
  } = useRightRail();
  const dispatch = useDispatch();
  const isAllowed = useGuard?.(props) ?? true;
  useEffect(() => {
    if (!isAllowed) {
      Logger.error(`Unauthorized access to right rail pane: ${pane}`, {
        props
      });
      dispatch(showError(I18n.t('publisher.components.right_rail.unauthorized_error')));
      close();
    }
  }, [close, dispatch, isAllowed, pane, props]);
  if (!isAllowed) {
    return null;
  }
  return <Component {...props} />;
};
const BaseOutlet = ({
  isOpen = false,
  railWidth,
  children
}) => {
  const ref = useRef();
  const {
    isCustomSideNav
  } = useLeftNavigationContext();
  const isProductRefreshAppShell = useProductRefreshAppShell();
  const isPhone = useDeviceType('isPhone');
  const isTabletDown = useDeviceType('isTabletDown');
  const isLargeDesktopDown = useDeviceType('isLargeDesktopDown');
  const pushWidth = useMemo(() => {
    if (isTabletDown || isLargeDesktopDown && isCustomSideNav) {
      return '0';
    }
    return railWidth;
  }, [isTabletDown, isLargeDesktopDown, isCustomSideNav, railWidth]);
  useFocusLock({
    isOpen: isOpen && isPhone,
    wrapperRef: ref
  });
  useDisableScrollBody({
    isOpen: isOpen && isPhone,
    wrapperRef: ref
  });
  return <Wrapper isOpen={isOpen} isPhone={isPhone} pushWidth={pushWidth} railWidth={isPhone ? '100vw' : railWidth}>
      <Container ref={ref} isOpen={isOpen} isPhone={isPhone} useLegacyViewportPosition={!isProductRefreshAppShell}>
        {children}
      </Container>
    </Wrapper>;
};
const RailOutlet = ({
  routeName,
  contentKey,
  component: Component,
  route,
  ...props
}) => {
  if (!route) {
    return <BaseOutlet isOpen={false} railWidth={route?.width ?? 0} />;
  }
  return <BaseOutlet isOpen railWidth={route.width}>
      <Content>
        <RailRenderer key={contentKey} pane={routeName} component={Component} useGuard={route.useGuard} {...props} />
      </Content>
    </BaseOutlet>;
};
export const Outlet = ({
  routes = defaultRoutes
}) => <BareOutlet renderer={RailOutlet} routes={routes} prefix={URL_STATE_PREFIX.RIGHT_RAIL} />;

require("./outlet.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./outlet.js");